// Functional programming utilties.

import assert from './assert'

export function PREVENT_DEFAULT(e: any) {
  e.preventDefault()
  e.stopPropagation()
}

/**
 * Returns a list of vals of length n.
 */
export function nOf<T>(n: number, val: T): T[] {
  return Array.from(Array(n)).map(() => val)
}

/**
 * Adds e to arr if e is not already in arr.
 */
export function addIfNotPresent<T>(arr: T[], e: T) {
  if (!arr.includes(e)) {
    arr.push(e)
  }
}

/**
 * Returns the only element of a Set or array. Throws if there is not exactly one.
 */
export function getOnlyElement<T>(collection: T[] | Set<T>) {
  const iter = collection.values()
  const val = iter.next()
  assert(!val.done, 'Expected at one element, found none')
  const ret = val.value
  assert(iter.next().done === true, 'Expected one element, found more than one')
  return ret
}

/**
 * Returns a function you can pass into Array.sort to lexicographicaly sort an
 * array by the value returned by the given function for each item.
 */
export function lex<T>(...getters: ((t: T) => number | string | boolean)[]): (a: T, b: T) => number {
  return (a: T, b: T) => {
    for (const getter of getters) {
      const ta = getter(a)
      const tb = getter(b)
      if (ta < tb) {
        return -1
      } else if (ta > tb) {
        return 1
      }
    }
    return 0
  }
}

/**
 * Array.map in reverse order
 *
 * The mapper function gets indices into the input array. These do not match indices in the output array.
 */
export function reverseMap<T, U>(arr: T[], mapper: (t: T, ix: number) => U): U[] {
  const ret = new Array(arr.length) as U[]
  for (let ix = arr.length - 1; ix >= 0; ix--) {
    ret[arr.length - ix - 1] = mapper(arr[ix], ix)
  }
  return ret
}

// Wait function to have a delay in ms. Should be used with caution / tests.
export function wait(delayMs: number): Promise<any> {
  return new Promise(resolve => {
    setTimeout(() => resolve(true), delayMs)
  })
}

export function groupBy<T>(array: T[], keyFunc: (t: T) => string | number) {
  const groups: { [key: string]: T[] } = {}

  for (const item of array) {
    const key = keyFunc(item)

    if (!groups[key]) {
      groups[key] = []
    }

    groups[key].push(item)
  }

  return groups
}

const states = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
]

export function getState(addr?: string | null): string | undefined {
  if (addr == null) {
    return undefined
  }
  const parts = /[^a-z]([a-z][a-z])[^a-z]*$/gi.exec(addr)
  const stateish = parts && parts[1]
  if (stateish && states.includes(stateish.toUpperCase())) {
    return stateish.toUpperCase()
  }
  return undefined
}

/**
 * Returns a random integer between min (inclusive) and max (inclusive).
 *
 * The value is no lower than min (or the next integer greater than min
 * if min isn't an integer) and no greater than max (or the next integer
 * lower than max if max isn't an integer).
 */
export function randomInt(min: number, max: number) {
  const minimum = Math.ceil(min)
  const maximum = Math.floor(max)
  return Math.floor(Math.random() * (maximum - minimum + 1)) + minimum
}

/**
 * Returns the power set of an array.
 *
 * https://www.tutorialspoint.com/finding-power-set-for-a-set-in-javascript-power-set
 */
export const powerSet = <T>(arr: T[]) => {
  const res: T[][] = []
  const { length } = arr
  const numberOfCombinations = 2 ** length
  for (let combinationIndex = 0; combinationIndex < numberOfCombinations; combinationIndex += 1) {
    const subSet: T[] = []
    for (let setElementIndex = 0; setElementIndex < arr.length; setElementIndex += 1) {
      if (combinationIndex & (1 << setElementIndex)) {
        subSet.push(arr[setElementIndex])
      }
    }
    res.push(subSet)
  }
  return res
}

/**
 * Returns a list containing the permutations of the given list.
 *
 * https://medium.com/weekly-webtips/step-by-step-guide-to-array-permutation-using-recursion-in-javascript-4e76188b88ff
 */
export function permute<T>(elems: T[]) {
  const result: T[][] = []
  if (elems.length === 0) {
    return []
  }
  if (elems.length === 1) {
    return [elems]
  }
  for (let i = 0; i < elems.length; i++) {
    const currentElem = elems[i]
    const remainingElems = elems.slice(0, i).concat(elems.slice(i + 1))
    const remainingElemsPermuted = permute(remainingElems)
    for (const e of remainingElemsPermuted) {
      const permutedArray = [currentElem].concat(e)
      result.push(permutedArray)
    }
  }
  return result
}

/**
 * Do nothing (block thread) for provided amount of time. Use carefully: you never want to block a function
 * in a customer-facing interface. Best used for server-side rate-limiting when hitting 3rd party APIs.
 */
export function sleep(millis: number) {
  return new Promise(resolve => setTimeout(resolve, millis))
}

/**
 * Make array of objects unique. Uniqueness is determined by the predicate.
 * If two objects are the same in terms of the predicate, choose the first one.
 *
 * @example
 * // returns [{ id: 1, title: 'a'}, { id: 2, title: 'b'}]
 * unique([{ id: 1, title: 'a'}, { id: 2, title: 'b'}, { id: 1, title: 'c'}], o => o.id.toString())
 */
export function unique<T>(arr: T[], predicate: (obj: T) => string) {
  const map: { [key: string]: T } = {}
  arr.forEach(o => {
    const key = predicate(o)
    map[key] = map[key] ? map[key] : o
  })
  return Object.values(map)
}

/**
 * Returns a pretty-printed JSON string of a given JavaScript object.
 * Auto-generated __typename and key/value pairs of null values are removed.
 * Passing in null returns JSON string of an empty object.
 */
export const stringifyAndRemoveNull = (object: any | null) => {
  if (!object) {
    return '{}'
  }
  return JSON.stringify(object, (key: string, value: any) => replacer(key, value), 2)
}

const replacer = (key: string, value: any) => {
  if (key === '__typename') {
    return undefined
  }

  // check to see if value is non-null or a boolean with a false value
  if (value || value === false) {
    return value
  } else {
    return undefined
  }
}
